/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Clock,
  Calendar,
  Layers,
  CheckCircle2,
  X,
  UserCheck,
  ShieldAlert,
  ArrowRight,
} from 'lucide-react';
import {
  PorteeChangement,
  ImpactChangement,
  ImpactReservation,
  ChangementPlan,
  ReservationPlan,
} from './types.ts';
import { heureDe } from './logiquePure.ts';

type Decision = 'replacer_auto' | 'garder' | 'contacter';
const ETIQUETTES: Record<Decision, string> = { replacer_auto: 'Replacer automatiquement', garder: 'Garder telle quelle', contacter: 'Contacter le client' };

interface ModalPorteeEtImpactProps {
  changements: ChangementPlan[];
  /** Impact initial (calculé pour « à partir de maintenant ») ; il est REDEMANDÉ à chaque changement de portée. */
  impact: ImpactChangement;
  reservations: ReservationPlan[];
  onFermer: () => void;
  onDemanderImpact: (portee: PorteeChangement) => Promise<ImpactChangement>;
  onConfirmerApplication: (portee: PorteeChangement, decisions: Record<string, Decision>) => Promise<void>;
}

const estInstallee = (r?: ReservationPlan) => r?.statut === 'arrivee' || r?.statut === 'walk_in';

/** Décision proposée d'abord : une réservation installée est GARDÉE par défaut ; les autres sont replacées si le serveur sait le faire. */
const decisionParDefaut = (aff: ImpactReservation, res?: ReservationPlan): Decision => {
  if (estInstallee(res) && aff.propositions.includes('garder')) return 'garder';
  if (aff.propositions.includes('replacer_auto')) return 'replacer_auto';
  return aff.propositions[0] ?? 'contacter';
};

export const ModalPorteeEtImpact: React.FC<ModalPorteeEtImpactProps> = ({
  changements,
  impact: impactInitial,
  reservations,
  onFermer,
  onDemanderImpact,
  onConfirmerApplication,
}) => {
  // Aucun choix par défaut dangereux -> null par défaut, l'utilisateur DOIT choisir explicitement
  const [porteeChoisie, setPorteeChoisie] = useState<PorteeChangement | null>(null);
  const [impact, setImpact] = useState<ImpactChangement>(impactInitial);
  const [impactEnCours, setImpactEnCours] = useState<boolean>(false);
  const [erreur, setErreur] = useState<string>('');
  const demande = useRef(0);

  // Décision par réservation touchée : replacer_auto / garder / contacter (seulement celles que le serveur propose)
  const [decisions, setDecisions] = useState<Record<string, Decision>>(() =>
    Object.fromEntries(impactInitial.reservationsTouchees.map((r) => [r.reservationId, decisionParDefaut(r, reservations.find((x) => x.id === r.reservationId))]))
  );
  const [estEnApplication, setEstEnApplication] = useState<boolean>(false);

  const choisirPortee = async (portee: PorteeChangement) => {
    setPorteeChoisie(portee);
    setErreur('');
    const numero = ++demande.current;
    setImpactEnCours(true);
    try {
      const nouvel = await onDemanderImpact(portee);
      if (numero !== demande.current) return; // une demande plus récente a pris le relais
      setImpact(nouvel);
      // On garde les choix déjà faits quand ils sont encore proposés ; sinon la décision par défaut.
      setDecisions((prev) => Object.fromEntries(nouvel.reservationsTouchees.map((r) => [r.reservationId,
        prev[r.reservationId] && r.propositions.includes(prev[r.reservationId]) ? prev[r.reservationId] : decisionParDefaut(r, reservations.find((x) => x.id === r.reservationId))])));
    } catch (e) {
      if (numero !== demande.current) return;
      setPorteeChoisie(null);
      setErreur(e instanceof Error && e.message ? e.message : 'Impossible de calculer l’effet de cette portée. Réessayez.');
    } finally {
      if (numero === demande.current) setImpactEnCours(false);
    }
  };

  // Clients déjà installés dont la décision n'est PAS « garder » : leur déplacement demande une confirmation explicite, qui les NOMME.
  const installeesDeplacees = useMemo(
    () => impact.reservationsTouchees.filter((aff) => estInstallee(reservations.find((r) => r.id === aff.reservationId)) && decisions[aff.reservationId] !== 'garder'),
    [impact, decisions, reservations]
  );
  const cleInstallees = installeesDeplacees.map((a) => a.reservationId).join('|');
  const [confirmationClientsAssis, setConfirmationClientsAssis] = useState<boolean>(false);
  useEffect(() => { setConfirmationClientsAssis(false); }, [cleInstallees]);
  const nomsInstallees = installeesDeplacees.map((aff) => {
    const res = reservations.find((r) => r.id === aff.reservationId);
    return `${res?.nom || aff.reservationId} (${res?.couverts ?? '?'} pers.)`;
  });

  const gererApplication = async () => {
    if (!porteeChoisie || impactEnCours) return;
    if (installeesDeplacees.length > 0 && !confirmationClientsAssis) return;

    setEstEnApplication(true);
    setErreur('');
    try {
      await onConfirmerApplication(porteeChoisie, decisions);
      onFermer();
    } catch (e) {
      setErreur(e instanceof Error && e.message ? e.message : 'Les modifications n’ont pas pu être appliquées.');
    } finally {
      setEstEnApplication(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/70 backdrop-blur-xs select-none"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-portee-titre"
    >
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl max-w-xl w-full max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95">
        {/* En-tête de la modale */}
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <div>
            <h2 id="modal-portee-titre" className="text-base sm:text-lg font-bold text-slate-900 dark:text-slate-100">
              Valider les modifications du plan
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {changements.length} modification{changements.length > 1 ? 's' : ''} en cours de validation
            </p>
          </div>
          <button
            type="button"
            onClick={onFermer}
            aria-label="Annuler et fermer"
            className="p-2 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 min-h-[44px] min-w-[44px] flex items-center justify-center cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Corps défilable */}
        <div className="p-5 overflow-y-auto space-y-5 text-sm">
          {/* ÉTAPE 1 : CHOIX EXPLICITE DE LA PORTÉE (AUCUN DÉFAUT DANGEREUX) */}
          <div className="space-y-2.5">
            <label className="block font-bold text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">
              1. Portée d'application requise :
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              <button
                type="button"
                onClick={() => void choisirPortee('maintenant')}
                className={`p-3 rounded-xl border text-left cursor-pointer transition-all flex flex-col justify-between min-h-[72px] ${
                  porteeChoisie === 'maintenant'
                    ? 'border-emerald-600 bg-emerald-50/70 dark:bg-emerald-950/40 text-content ring-2 ring-emerald-500'
                    : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 bg-slate-50/50 dark:bg-slate-800/40'
                }`}
              >
                <div className="flex items-center gap-1.5 font-bold text-xs">
                  <Clock className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>À partir de maintenant</span>
                </div>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                  Valable pour les tables en cours et les prochaines minutes.
                </p>
              </button>

              <button
                type="button"
                onClick={() => void choisirPortee('service')}
                className={`p-3 rounded-xl border text-left cursor-pointer transition-all flex flex-col justify-between min-h-[72px] ${
                  porteeChoisie === 'service'
                    ? 'border-indigo-600 bg-indigo-50/70 dark:bg-indigo-950/40 text-content ring-2 ring-indigo-500'
                    : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 bg-slate-50/50 dark:bg-slate-800/40'
                }`}
              >
                <div className="flex items-center gap-1.5 font-bold text-xs">
                  <Calendar className="w-4 h-4 text-indigo-600 shrink-0" />
                  <span>Pour tout le service</span>
                </div>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                  S'applique à toutes les réservations jusqu'à la fermeture du service.
                </p>
              </button>

              <button
                type="button"
                onClick={() => void choisirPortee('permanent')}
                className={`p-3 rounded-xl border text-left cursor-pointer transition-all flex flex-col justify-between min-h-[72px] ${
                  porteeChoisie === 'permanent'
                    ? 'border-purple-600 bg-purple-50/70 dark:bg-purple-950/40 text-content ring-2 ring-purple-500'
                    : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 bg-slate-50/50 dark:bg-slate-800/40'
                }`}
              >
                <div className="flex items-center gap-1.5 font-bold text-xs">
                  <Layers className="w-4 h-4 text-purple-600 shrink-0" />
                  <span>De façon permanente</span>
                </div>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                  Enregistre la configuration définitivement pour les prochains jours.
                </p>
              </button>
            </div>
          </div>

          {/* ÉTAPE 2 : APERÇU DE L'EFFET SUR LES RÉSERVATIONS */}
          <div className="space-y-2.5">
            <label className="block font-bold text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">
              2. Réservations impactées{porteeChoisie ? ` (${impact.reservationsTouchees.length})` : ''} :
            </label>

            {!porteeChoisie ? (
              <p className="p-3 rounded-xl border border-dashed border-slate-300 dark:border-slate-700 text-xs text-slate-500 dark:text-slate-400">
                Choisissez d’abord la portée : l’effet sur les réservations en dépend.
              </p>
            ) : impact.reservationsTouchees.length === 0 ? (
              <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 flex items-center gap-2 text-xs text-content">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>Aucune réservation existante n'est compromise par ces modifications.</span>
              </div>
            ) : (
              <div className="space-y-2.5 max-h-56 overflow-y-auto pr-1">
                {impact.reservationsTouchees.map((aff) => {
                  const res = reservations.find((r) => r.id === aff.reservationId);
                  const decisionActuelle = decisions[aff.reservationId] || decisionParDefaut(aff, res);
                  const estAssis = estInstallee(res);

                  return (
                    <div
                      key={aff.reservationId}
                      className={`p-3 rounded-xl border text-xs space-y-2 ${
                        estAssis
                          ? 'border-amber-300 bg-amber-50/70 dark:bg-amber-950/40 dark:border-amber-800'
                          : 'border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/40'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-bold text-slate-900 dark:text-slate-100 flex items-center gap-1.5">
                          <UserCheck className="w-3.5 h-3.5 text-slate-500" />
                          <span>{res?.nom || aff.reservationId}</span>
                          <span className="text-slate-500 font-normal">
                            ({res?.couverts} pers. à {res ? heureDe(res.debut) : ''})
                          </span>
                        </div>

                        {estAssis && (
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-200 text-amber-900 dark:bg-amber-900 dark:text-amber-200">
                            Client déjà installé
                          </span>
                        )}
                      </div>

                      <p className="text-[11px] text-slate-600 dark:text-slate-400">
                        Motif : {aff.raison}
                      </p>

                      {/* Les propositions viennent du serveur : « garder » n'est pas offert quand la table est supprimée, « replacer » quand aucune table ne convient */}
                      <div className="flex flex-wrap items-center gap-1.5 pt-1" role="group" aria-label={`Que faire de la réservation de ${res?.nom || aff.reservationId} ?`}>
                        {aff.propositions.map((p) => (
                          <button
                            key={p}
                            type="button"
                            aria-pressed={decisionActuelle === p}
                            onClick={() => setDecisions((prev) => ({ ...prev, [aff.reservationId]: p }))}
                            className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold cursor-pointer min-h-[38px] transition-colors ${
                              decisionActuelle === p
                                ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                                : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200'
                            }`}
                          >
                            {ETIQUETTES[p]}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {impactEnCours && <p role="status" className="text-[11px] text-slate-500 dark:text-slate-400">Calcul de l’effet pour cette portée…</p>}
          </div>

          {porteeChoisie && impact.avertissements.length > 0 && (
            <ul className="p-3 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 space-y-1 text-[11px] text-amber-900 dark:text-amber-200" aria-label="Avertissements">
              {impact.avertissements.map((a) => (<li key={a} className="flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" /><span>{a}</span></li>))}
            </ul>
          )}

          {porteeChoisie && Object.keys(impact.capaciteParTaille).length > 0 && (
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              Après ces modifications, emplacements disponibles :{' '}
              {Object.entries(impact.capaciteParTaille).map(([taille, n]) => `${taille} pers. : ${n}`).join(' · ')}
            </p>
          )}

          {/* SÉCURITÉ CRITIQUE : DÉPLACEMENT DE CLIENTS INSTALLÉS */}
          {installeesDeplacees.length > 0 && (
            <div className="p-3 rounded-xl bg-red-50 dark:bg-red-950/50 border border-red-300 dark:border-red-800 space-y-2">
              <div className="flex items-center gap-2 text-red-700 dark:text-red-300 font-bold text-xs">
                <ShieldAlert className="w-4 h-4 shrink-0" />
                <span>Sécurité service : confirmation requise</span>
              </div>
              <p className="text-[11px] text-red-800 dark:text-red-200 leading-relaxed">
                Des clients sont <strong>déjà attablés dans la salle</strong> : {nomsInstallees.join(', ')}. Vous devez confirmer expressément que
                le maître d'hôtel a convenu de la réorganisation physique.
              </p>
              <label className="flex items-center gap-2 pt-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmationClientsAssis}
                  onChange={(e) => setConfirmationClientsAssis(e.target.checked)}
                  className="w-4 h-4 rounded text-red-600 focus:ring-red-500"
                />
                <span className="text-xs font-semibold text-slate-900 dark:text-slate-100">
                  Je confirme avoir validé le déplacement avec les clients installés : {nomsInstallees.join(', ')}
                </span>
              </label>
            </div>
          )}
        </div>

        {erreur && (
          <p role="alert" className="mx-5 mb-3 p-3 rounded-xl bg-red-50 dark:bg-red-950/50 border border-red-300 dark:border-red-800 text-xs font-semibold text-red-800 dark:text-red-200">
            {erreur}
          </p>
        )}

        {/* Pied de page et boutons d'action */}
        <div className="px-5 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 flex flex-wrap items-center justify-between gap-2.5">
          <button
            type="button"
            onClick={onFermer}
            aria-label="Revenir au plan sans appliquer"
            className="px-4 py-2.5 rounded-xl border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 font-semibold text-xs sm:text-sm text-slate-700 dark:text-slate-300 cursor-pointer min-h-[44px] transition-colors"
          >
            Revenir au plan
          </button>

          <button
            type="button"
            onClick={gererApplication}
            disabled={
              !porteeChoisie ||
              impactEnCours ||
              estEnApplication ||
              (installeesDeplacees.length > 0 && !confirmationClientsAssis)
            }
            aria-label="Appliquer les modifications validées"
            className="px-5 py-2.5 rounded-xl bg-slate-950 text-white dark:bg-slate-100 dark:text-slate-900 hover:opacity-90 active:scale-95 font-bold text-xs sm:text-sm cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed min-h-[44px] shadow-md transition-all flex items-center gap-2"
          >
            {estEnApplication ? (
              <span>Application en cours...</span>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <span>Appliquer les changements</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
